/**
 * WhatsApp Inbox
 * A live inbox over the WhatsApp AI-bot module. Left: conversations. Right: the
 * selected thread + compose box.
 *
 * Two things every home-built WhatsApp inbox gets wrong, handled here:
 *  - 24-hour window: outside it only approved templates deliver, so the compose
 *    box is disabled and a countdown is shown instead of letting a doomed
 *    free-form reply silently fail.
 *  - Bot muting: a reply typed on a teammate's phone (echo) or a CRM takeover
 *    mutes the bot automatically; the header shows who currently owns the thread.
 *
 * Live updates are by polling (SSE can replace this later without touching the
 * UI). Contacts refresh every 12s; the open thread every 8s.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Card, Input, Button, List, Avatar, Badge, Tag, Typography, Space, Empty, Spin,
  Alert, Tooltip, message,
} from 'antd';
import {
  SendOutlined, ReloadOutlined, RobotOutlined, UserOutlined, WhatsAppOutlined,
  ClockCircleOutlined, TeamOutlined,
} from '@ant-design/icons';
import {
  fetchWhatsAppContacts, fetchWhatsAppThread, sendWhatsAppMessage, takeoverWhatsAppThread,
} from '../api';

const { Text, Title } = Typography;

const CONTACTS_POLL_MS = 12000;
const THREAD_POLL_MS = 8000;

const fmtTime = (d) => (d ? new Date(d).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '');

// Human "3h 12m left" for the 24-hour window.
function windowCountdown(expiresAt) {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt) - Date.now();
  if (ms <= 0) return null;
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m left` : `${m}m left`;
}

const ORIGIN_META = {
  bot: { color: 'green', icon: <RobotOutlined />, label: 'Bot' },
  agent: { color: 'blue', icon: <UserOutlined />, label: 'Agent' },
  system: { color: 'gold', icon: <TeamOutlined />, label: 'System' },
  customer: { color: 'default', icon: <UserOutlined />, label: 'Customer' },
};

function MessageBubble({ msg }) {
  const mine = msg.direction === 'outbound' || msg.direction === 'echo';
  const om = ORIGIN_META[msg.origin] || ORIGIN_META.customer;
  return (
    <div style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
      <div style={{
        maxWidth: '72%', padding: '8px 12px', borderRadius: 10,
        background: mine ? (msg.origin === 'bot' ? '#dcf8c6' : '#d6e4ff') : '#f5f5f5',
        border: '1px solid #eee',
      }}
      >
        {mine && (
          <div style={{ marginBottom: 2 }}>
            <Tag color={om.color} icon={om.icon} style={{ marginInlineEnd: 0 }}>
              {msg.direction === 'echo' ? 'Phone reply' : om.label}
            </Tag>
          </div>
        )}
        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {msg.text || <Text type="secondary">[{msg.type}]</Text>}
        </div>
        <div style={{ marginTop: 2, textAlign: 'right' }}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {fmtTime(msg.createdAt)}
            {mine && msg.status ? ` · ${msg.status}` : ''}
          </Text>
        </div>
      </div>
    </div>
  );
}

export default function WhatsAppInbox() {
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [selected, setSelected] = useState(null); // waId
  const [thread, setThread] = useState(null);      // { contact, messages }
  const [loadingThread, setLoadingThread] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const scrollRef = useRef(null);
  const selectedRef = useRef(null);
  selectedRef.current = selected;

  const loadContacts = async () => {
    try {
      const res = await fetchWhatsAppContacts({ limit: 100 });
      // Backend wraps payloads as { success, data }.
      setContacts(res.data?.data || []);
    } catch (err) {
      messageApi.error(err.response?.data?.message || 'Failed to load conversations');
    } finally {
      setLoadingContacts(false);
    }
  };

  const loadThread = async (waId, { quiet } = {}) => {
    if (!waId) return;
    if (!quiet) setLoadingThread(true);
    try {
      const res = await fetchWhatsAppThread(waId, { limit: 200 });
      // Ignore a late response if the user switched conversations meanwhile.
      if (selectedRef.current === waId) setThread(res.data?.data || null);
    } catch (err) {
      if (!quiet) messageApi.error(err.response?.data?.message || 'Failed to load conversation');
    } finally {
      if (!quiet) setLoadingThread(false);
    }
  };

  // Initial load + contacts polling.
  useEffect(() => {
    loadContacts();
    const t = setInterval(loadContacts, CONTACTS_POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  // Thread polling for the open conversation.
  useEffect(() => {
    if (!selected) return undefined;
    loadThread(selected);
    const t = setInterval(() => loadThread(selectedRef.current, { quiet: true }), THREAD_POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [selected]);

  // Keep the thread scrolled to the latest message.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || !selected) return;
    setSending(true);
    try {
      await sendWhatsAppMessage(selected, text);
      setDraft('');
      await loadThread(selected, { quiet: true });
      loadContacts();
    } catch (err) {
      messageApi.error(err.response?.data?.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleTakeover = async () => {
    if (!selected) return;
    try {
      await takeoverWhatsAppThread(selected);
      messageApi.success('Bot muted — you have taken over this thread');
      loadThread(selected, { quiet: true });
    } catch (err) {
      messageApi.error(err.response?.data?.message || 'Failed to take over');
    }
  };

  const c = thread?.contact;
  const windowOpen = !!c?.windowOpen;
  const countdown = windowCountdown(c?.windowExpiresAt);

  return (
    <div style={{ padding: 16 }}>
      {contextHolder}
      <Title level={3} style={{ marginBottom: 12 }}>
        <WhatsAppOutlined style={{ color: '#25D366' }} /> WhatsApp Inbox
      </Title>

      <div style={{ display: 'flex', gap: 12, height: 'calc(100vh - 160px)' }}>
        {/* ── Conversations ── */}
        <Card
          size="small"
          style={{ width: 320, flexShrink: 0, display: 'flex', flexDirection: 'column' }}
          styles={{ body: { padding: 0, overflowY: 'auto', flex: 1 } }}
          title={`Conversations (${contacts.length})`}
          extra={<Button size="small" type="text" icon={<ReloadOutlined />} onClick={loadContacts} />}
        >
          {loadingContacts ? (
            <div style={{ padding: 32, textAlign: 'center' }}><Spin /></div>
          ) : contacts.length === 0 ? (
            <Empty description="No conversations yet" style={{ marginTop: 40 }} />
          ) : (
            <List
              dataSource={contacts}
              rowKey={(item) => item.waId}
              renderItem={(item) => (
                <List.Item
                  onClick={() => setSelected(item.waId)}
                  style={{
                    cursor: 'pointer', padding: '10px 14px',
                    background: selected === item.waId ? '#e6f7ff' : 'transparent',
                  }}
                >
                  <List.Item.Meta
                    avatar={(
                      <Badge count={item.unreadCount || 0} size="small">
                        <Avatar style={{ background: '#25D366' }} icon={<WhatsAppOutlined />} />
                      </Badge>
                    )}
                    title={(
                      <Space size={4}>
                        <Text strong ellipsis style={{ maxWidth: 150 }}>
                          {item.profileName || item.waId}
                        </Text>
                        {item.windowOpen
                          ? <Tooltip title="24h window open"><Badge status="success" /></Tooltip>
                          : <Tooltip title="Window closed"><Badge status="default" /></Tooltip>}
                        {item.botMuted && <Tag color="orange" style={{ marginInlineEnd: 0 }}>muted</Tag>}
                        {item.optedOut && <Tag color="red" style={{ marginInlineEnd: 0 }}>opted out</Tag>}
                      </Space>
                    )}
                    description={(
                      <Text type="secondary" ellipsis style={{ fontSize: 12 }}>
                        {item.lastMessagePreview || '—'}
                      </Text>
                    )}
                  />
                </List.Item>
              )}
            />
          )}
        </Card>

        {/* ── Thread ── */}
        <Card
          size="small"
          style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
          styles={{ body: { display: 'flex', flexDirection: 'column', flex: 1, padding: 0, minHeight: 0 } }}
        >
          {!selected ? (
            <div style={{ margin: 'auto' }}>
              <Empty description="Select a conversation" />
            </div>
          ) : (
            <>
              {/* Header */}
              <div style={{ padding: '10px 16px', borderBottom: '1px solid #f0f0f0' }}>
                <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
                  <Space direction="vertical" size={0}>
                    <Text strong>{c?.profileName || selected}</Text>
                    <Space size={6}>
                      <Text type="secondary" style={{ fontSize: 12 }}>+{selected}</Text>
                      {c?.crmLead && (
                        <Tag color="geekblue">Lead {c.crmLead.reference || ''}</Tag>
                      )}
                    </Space>
                  </Space>
                  <Space>
                    {windowOpen
                      ? <Tag icon={<ClockCircleOutlined />} color="green">{countdown || 'Window open'}</Tag>
                      : <Tag icon={<ClockCircleOutlined />} color="red">Window closed</Tag>}
                    {c?.botMuted
                      ? <Tag color="orange" icon={<UserOutlined />}>Human</Tag>
                      : <Tag color="green" icon={<RobotOutlined />}>Bot active</Tag>}
                    {!c?.botMuted && (
                      <Button size="small" onClick={handleTakeover}>Take over</Button>
                    )}
                  </Space>
                </Space>
              </div>

              {/* Messages */}
              <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 16, minHeight: 0 }}>
                {loadingThread && !thread ? (
                  <div style={{ textAlign: 'center', marginTop: 40 }}><Spin /></div>
                ) : (
                  (thread?.messages || []).map((m) => <MessageBubble key={m._id} msg={m} />)
                )}
              </div>

              {/* Compose */}
              <div style={{ borderTop: '1px solid #f0f0f0', padding: 12 }}>
                {!windowOpen && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 8 }}
                    message="24-hour window closed"
                    description="Free-form messages won't deliver. Only an approved template can reopen this conversation (send from WhatsApp Manager for now)."
                  />
                )}
                <Space.Compact style={{ width: '100%' }}>
                  <Input.TextArea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder={windowOpen ? 'Type a reply…' : 'Window closed — reply disabled'}
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    disabled={!windowOpen || sending}
                    onPressEnter={(e) => { if (!e.shiftKey) { e.preventDefault(); handleSend(); } }}
                  />
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    loading={sending}
                    disabled={!windowOpen || !draft.trim()}
                    onClick={handleSend}
                  >
                    Send
                  </Button>
                </Space.Compact>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Sending here mutes the bot on this thread. Enter to send, Shift+Enter for a new line.
                </Text>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
